import React, { useState } from "react";
import { Card, Col, Row, Button, Form, InputGroup } from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function PaycheckCalculator() {
  const [grossPay, setGrossPay] = useState("");
  const [deductions, setDeductions] = useState("");
  const [bonuses, setBonuses] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [netPay, setNetPay] = useState(null);

  const calculateNetPay = () => {
    const gross = parseFloat(grossPay) || 0;
    const totalDeductions = parseFloat(deductions) || 0;
    const totalBonuses = parseFloat(bonuses) || 0;
    const tax = parseFloat(taxRate) || 0;

    const taxableAmount = gross + totalBonuses - totalDeductions;
    const taxAmount = taxableAmount * (tax / 100);
    const finalPay = taxableAmount - taxAmount;

    setNetPay(finalPay.toFixed(2));
  };

  const resetCalculator = () => {
    setGrossPay("");
    setDeductions("");
    setBonuses("");
    setTaxRate("");
    setNetPay(null);
  };

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-calculator-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Paycheck Calculator</h1>
          </div>
          <p className="lead text-muted">Calculate your take-home pay with ease</p>
        </div>

        <Row className="g-4">
          <Col lg="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
              <div className="bg-gradient-primary text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-calculator-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Calculate Your Paycheck</h5>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="ri-money-dollar-circle-line me-2"></i>Gross Pay
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-primary text-white border-primary">
                        <i className="ri-dollar-sign"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="Enter gross pay"
                        value={grossPay}
                        onChange={(e) => setGrossPay(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '0 12px 12px 0' }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="ri-subtract-line me-2"></i>Deductions
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-danger text-white border-danger">
                        <i className="ri-dollar-sign"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="Enter deductions"
                        value={deductions}
                        onChange={(e) => setDeductions(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '0 12px 12px 0' }}
                      />
                    </InputGroup>
                    <Form.Text className="text-muted mt-2">
                      <i className="ri-information-line me-1"></i>
                      Example: Insurance, Retirement Contributions, etc.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="ri-add-line me-2"></i>Bonuses
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text className="bg-success text-white border-success">
                        <i className="ri-dollar-sign"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="number"
                        placeholder="Enter bonuses"
                        value={bonuses}
                        onChange={(e) => setBonuses(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '0 12px 12px 0' }}
                      />
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold">
                      <i className="ri-percent-line me-2"></i>Tax Rate
                    </Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        placeholder="Enter tax rate (in %)"
                        value={taxRate}
                        onChange={(e) => setTaxRate(e.target.value)}
                        className="form-control-lg shadow-sm border-2"
                        style={{ borderRadius: '12px 0 0 12px' }}
                      />
                      <InputGroup.Text className="bg-warning text-dark border-warning">
                        <i className="ri-percent"></i>
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Form>
              </Card.Body>
              <Card.Footer className="border-0 p-4 bg-light">
                <div className="d-flex gap-3">
                  <Button 
                    variant="outline-secondary" 
                    onClick={resetCalculator}
                    className="flex-fill py-3 rounded-pill fw-semibold"
                  >
                    <i className="ri-refresh-line me-2"></i>
                    Reset
                  </Button>
                  <Button 
                    variant="primary" 
                    onClick={calculateNetPay}
                    className="flex-fill py-3 rounded-pill fw-semibold shadow-lg"
                  >
                    <i className="ri-calculator-line me-2"></i>
                    Calculate
                  </Button>
                </div>
              </Card.Footer>
            </Card>
          </Col>

          <Col lg="6">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100">
              <div className="bg-gradient-success text-white p-4" 
                   style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-money-dollar-box-line fs-4 me-3"></i>
                  <h5 className="mb-0 fw-bold">Paycheck Result</h5>
                </div>
              </div>
              <Card.Body className="p-5 text-center d-flex flex-column justify-content-center">
                {netPay !== null ? (
                  <div className="paycheck-result">
                    <div className="mb-4">
                      <i className="ri-money-dollar-circle-line text-success" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h4 className="text-success mb-3 fw-bold">Your Net Pay</h4>
                    <div className="bg-white rounded-4 p-4 shadow-lg mb-4">
                      <h1 className="text-success display-3 fw-bold mb-0">
                        ${netPay}
                      </h1>
                    </div>
                    <p className="text-muted mb-0">
                      <i className="ri-information-line me-2"></i>
                      This is your estimated take-home pay.
                    </p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4">
                      <i className="ri-calculator-line text-muted" style={{ fontSize: '4rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-3">
                      Ready to see your <span className="text-primary fw-bold">take-home pay?</span>
                    </h5>
                    <p className="text-muted">
                      <i className="ri-arrow-up-line me-2"></i>
                      Enter your details to calculate your paycheck.
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Additional Information */}
        <Row className="g-4 mt-5">
          <Col lg="4">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-primary text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-money-dollar-circle-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">What is Gross Pay?</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Gross pay is the total amount of money you earn before taxes,
                  deductions, or contributions are taken out.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg="4">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-danger text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-subtract-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">What are Deductions?</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Deductions are amounts taken out of your paycheck, such as
                  insurance premiums, retirement contributions, and more.
                </p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg="4">
            <Card className="border-0 shadow-lg rounded-4 overflow-hidden h-100"
                  style={{ 
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                  }}>
              <div className="bg-gradient-warning text-white p-3" 
                   style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
                <div className="d-flex align-items-center">
                  <i className="ri-percent-line fs-4 me-3"></i>
                  <h6 className="mb-0 fw-bold">How is Tax Calculated?</h6>
                </div>
              </div>
              <Card.Body className="p-4">
                <p className="text-muted mb-0">
                  Taxes are calculated based on the taxable income after adding
                  bonuses and subtracting deductions.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </React.Fragment>
  );
}
